import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  GitBranch, Plus, Edit, Trash2, ArrowRight, CheckCircle, Settings2,
  Building2, Users, Shield, ArrowDown, GripVertical, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const AVAILABLE_ROLES = [
  { code: 'hod', name: 'Head of Department', color: 'bg-blue-500' },
  { code: 'principal', name: 'Principal', color: 'bg-purple-500' },
  { code: 'dean', name: 'Dean', color: 'bg-indigo-500' },
  { code: 'hr', name: 'HR Manager', color: 'bg-green-500' },
  { code: 'admin', name: 'Administrator', color: 'bg-orange-500' },
  { code: 'finance', name: 'Finance Manager', color: 'bg-yellow-500' },
];

export default function ApprovalHierarchy() {
  const { entities } = useOutletContext() || {};
  const { user } = useAuth();
  const [hierarchies, setHierarchies] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [activeHierarchy, setActiveHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingHierarchy, setEditingHierarchy] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (entities?.length > 0 && !selectedEntity) {
      setSelectedEntity(entities[0].entity_id);
    }
  }, [entities]);

  useEffect(() => {
    if (selectedEntity) {
      fetchHierarchyForEntity();
    }
  }, [selectedEntity]);

  const fetchHierarchyForEntity = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hrms-token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch active hierarchy for selected entity
      const response = await fetch(
        `${API_URL}/api/approval-hierarchies/entity/${selectedEntity}/active`,
        { headers, credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setActiveHierarchy(data);
      }
      
      // Fetch all hierarchies for this entity
      const allResponse = await fetch(
        `${API_URL}/api/approval-hierarchies?entity_id=${selectedEntity}`,
        { headers, credentials: 'include' }
      );
      
      if (allResponse.ok) {
        setHierarchies(await allResponse.json());
      }
    } catch (error) {
      console.error('Failed to fetch hierarchy:', error);
      toast.error('Failed to load approval hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHierarchy = async () => {
    if (!editingHierarchy) return;
    
    try {
      const token = localStorage.getItem('hrms-token');
      const isNew = editingHierarchy.hierarchy_id === 'new';
      
      const url = isNew 
        ? `${API_URL}/api/approval-hierarchies`
        : `${API_URL}/api/approval-hierarchies/${editingHierarchy.hierarchy_id}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const payload = {
        entity_id: selectedEntity,
        name: editingHierarchy.name,
        description: editingHierarchy.description,
        levels: editingHierarchy.levels,
        is_default: editingHierarchy.is_default
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success(isNew ? 'Approval hierarchy created' : 'Approval hierarchy updated');
        setIsEditing(false);
        setEditingHierarchy(null);
        fetchHierarchyForEntity();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save hierarchy');
      }
    } catch (error) {
      toast.error('Failed to save hierarchy');
    }
  };

  const handleDeleteHierarchy = async (hierarchyId) => {
    if (!confirm('Are you sure you want to delete this approval hierarchy?')) return;
    
    try {
      const token = localStorage.getItem('hrms-token');
      const response = await fetch(`${API_URL}/api/approval-hierarchies/${hierarchyId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Hierarchy deleted');
        fetchHierarchyForEntity();
      } else {
        toast.error('Failed to delete hierarchy');
      }
    } catch (error) {
      toast.error('Failed to delete hierarchy');
    }
  };

  const startEditing = (hierarchy = null) => {
    if (hierarchy) {
      setEditingHierarchy({ ...hierarchy });
    } else {
      // Create new hierarchy
      setEditingHierarchy({
        hierarchy_id: 'new',
        entity_id: selectedEntity,
        name: 'Custom Approval Flow',
        description: '',
        is_default: false,
        levels: [
          { level_id: 'L1', level_name: 'Department Approval', roles: ['hod'], order: 1, is_required: true },
          { level_id: 'L2', level_name: 'Institution Approval', roles: ['principal'], order: 2, is_required: true },
          { level_id: 'L3', level_name: 'HR Final Approval', roles: ['hr'], order: 3, is_required: true },
        ]
      });
    }
    setIsEditing(true);
  };

  const addLevel = () => {
    if (!editingHierarchy) return;
    const newOrder = editingHierarchy.levels.length + 1;
    setEditingHierarchy({
      ...editingHierarchy,
      levels: [
        ...editingHierarchy.levels,
        {
          level_id: `L${newOrder}`,
          level_name: `Level ${newOrder} Approval`,
          roles: [],
          order: newOrder,
          is_required: true
        }
      ]
    });
  };

  const removeLevel = (index) => {
    if (!editingHierarchy) return;
    const newLevels = editingHierarchy.levels.filter((_, i) => i !== index);
    // Reorder
    newLevels.forEach((level, i) => {
      level.order = i + 1;
      level.level_id = `L${i + 1}`;
    });
    setEditingHierarchy({ ...editingHierarchy, levels: newLevels });
  };

  const updateLevel = (index, field, value) => {
    if (!editingHierarchy) return;
    const newLevels = [...editingHierarchy.levels];
    newLevels[index] = { ...newLevels[index], [field]: value };
    setEditingHierarchy({ ...editingHierarchy, levels: newLevels });
  };

  const toggleRoleInLevel = (levelIndex, roleCode) => {
    if (!editingHierarchy) return;
    const newLevels = [...editingHierarchy.levels];
    const currentRoles = newLevels[levelIndex].roles || [];
    
    if (currentRoles.includes(roleCode)) {
      newLevels[levelIndex].roles = currentRoles.filter(r => r !== roleCode);
    } else {
      newLevels[levelIndex].roles = [...currentRoles, roleCode];
    }
    
    setEditingHierarchy({ ...editingHierarchy, levels: newLevels });
  };

  const getEntityName = (entityId) => {
    const entity = entities?.find(e => e.entity_id === entityId);
    return entity?.name || entityId;
  };

  const getRoleBadge = (roleCode) => {
    const role = AVAILABLE_ROLES.find(r => r.code === roleCode);
    return role ? (
      <Badge key={roleCode} className={`${role.color} text-white text-xs mr-1`}>
        {role.name}
      </Badge>
    ) : (
      <Badge key={roleCode} variant="outline" className="text-xs mr-1">{roleCode}</Badge>
    );
  };

  // Visual Flow Component
  const ApprovalFlowVisual = ({ hierarchy, compact = false }) => {
    if (!hierarchy?.levels?.length) return null;
    
    const sortedLevels = [...hierarchy.levels].sort((a, b) => a.order - b.order);
    
    return (
      <div className={`flex ${compact ? 'items-center gap-2' : 'flex-col items-center gap-4'}`}>
        {/* Start Node */}
        <div className={`flex items-center gap-2 ${compact ? '' : 'mb-2'}`}>
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          {!compact && <span className="text-sm font-medium">Employee Submits</span>}
        </div>
        
        {sortedLevels.map((level, idx) => (
          <React.Fragment key={level.level_id}>
            {/* Arrow */}
            {compact ? (
              <ArrowRight className="w-4 h-4 text-slate-400" />
            ) : (
              <ArrowDown className="w-5 h-5 text-slate-400" />
            )}
            
            {/* Level Node */}
            <div className={`${compact ? 'px-3 py-1' : 'p-4 w-full max-w-md'} bg-white border-2 border-slate-200 rounded-lg ${!compact && 'shadow-sm'}`}>
              <div className={`flex ${compact ? 'items-center gap-2' : 'flex-col'}`}>
                <div className={`flex items-center gap-2 ${!compact && 'mb-2'}`}>
                  <Badge variant="outline" className="bg-slate-100">
                    {level.level_id}
                  </Badge>
                  <span className={`font-medium ${compact ? 'text-xs' : 'text-sm'}`}>
                    {level.level_name}
                  </span>
                </div>
                {!compact && (
                  <div className="flex flex-wrap gap-1">
                    {level.roles?.map(role => getRoleBadge(role))}
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
        
        {/* End Node */}
        {compact ? (
          <ArrowRight className="w-4 h-4 text-slate-400" />
        ) : (
          <ArrowDown className="w-5 h-5 text-slate-400" />
        )}
        <div className={`flex items-center gap-2 ${compact ? '' : 'mt-2'}`}>
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          {!compact && <span className="text-sm font-medium text-green-600">Approved</span>}
        </div>
      </div>
    );
  };

  if (!isSuperAdmin) {
    // Read-only view for non-super-admins
    return (
      <div className="space-y-6 animate-fade-in" data-testid="approval-hierarchy-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Approval Workflow
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            View the leave approval hierarchy for your organization
          </p>
        </div>

        {/* Entity Selector */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Building2 className="w-5 h-5 text-slate-500" />
              <Select value={selectedEntity} onValueChange={setSelectedEntity}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities?.map(entity => (
                    <SelectItem key={entity.entity_id} value={entity.entity_id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Visual Flow Display */}
        {activeHierarchy && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-[#DC2626]" />
                {activeHierarchy.name}
              </CardTitle>
              <CardDescription>
                {activeHierarchy.description || 'Active approval workflow for leave requests'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ApprovalFlowVisual hierarchy={activeHierarchy} />
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approval-hierarchy-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
            Approval Hierarchy Configuration
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Configure leave approval workflows per entity • <span className="text-[#DC2626]">Super Admin Only</span>
          </p>
        </div>
        {!isEditing && (
          <Button className="btn-primary" onClick={() => startEditing()} data-testid="create-hierarchy-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create New Hierarchy
          </Button>
        )}
      </div>

      {/* Entity Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Building2 className="w-5 h-5 text-slate-500" />
            <Label>Select Entity:</Label>
            <Select value={selectedEntity} onValueChange={setSelectedEntity} disabled={isEditing}>
              <SelectTrigger className="w-[300px]" data-testid="entity-selector">
                <SelectValue placeholder="Select entity" />
              </SelectTrigger>
              <SelectContent>
                {entities?.map(entity => (
                  <SelectItem key={entity.entity_id} value={entity.entity_id}>
                    {entity.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isEditing ? (
        /* Editing Mode */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#DC2626]" />
              {editingHierarchy?.hierarchy_id === 'new' ? 'Create New' : 'Edit'} Approval Hierarchy
            </CardTitle>
            <CardDescription>
              Configure the approval levels and roles for {getEntityName(selectedEntity)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hierarchy Name</Label>
                <Input
                  value={editingHierarchy?.name || ''}
                  onChange={(e) => setEditingHierarchy({ ...editingHierarchy, name: e.target.value })}
                  placeholder="e.g., Standard Approval Flow"
                  data-testid="hierarchy-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingHierarchy?.description || ''}
                  onChange={(e) => setEditingHierarchy({ ...editingHierarchy, description: e.target.value })}
                  placeholder="Brief description..."
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={editingHierarchy?.is_default || false}
                onCheckedChange={(checked) => setEditingHierarchy({ ...editingHierarchy, is_default: checked })}
              />
              <Label>Set as default hierarchy for this entity</Label>
            </div>

            {/* Approval Levels */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Approval Levels</h3>
                <Button size="sm" variant="outline" onClick={addLevel} data-testid="add-level-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Level
                </Button>
              </div>
              
              {editingHierarchy?.levels?.map((level, idx) => (
                <div key={idx} className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-800" data-testid={`level-${idx}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
                      <Badge variant="outline" className="bg-white">{level.level_id}</Badge>
                    </div>
                    
                    <div className="flex-1 grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Level Name</Label>
                        <Input
                          value={level.level_name}
                          onChange={(e) => updateLevel(idx, 'level_name', e.target.value)}
                          placeholder="e.g., Department Approval"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Description</Label>
                        <Input
                          value={level.description || ''}
                          onChange={(e) => updateLevel(idx, 'description', e.target.value)}
                          placeholder="Brief description..."
                        />
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500"
                      onClick={() => removeLevel(idx)}
                      disabled={editingHierarchy.levels.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Role Selection */}
                  <div className="mt-4">
                    <Label className="text-xs mb-2 block">Roles that can approve at this level:</Label>
                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_ROLES.map(role => (
                        <Button
                          key={role.code}
                          size="sm"
                          variant={level.roles?.includes(role.code) ? 'default' : 'outline'}
                          className={level.roles?.includes(role.code) ? `${role.color} text-white` : ''}
                          onClick={() => toggleRoleInLevel(idx, role.code)}
                        >
                          {role.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview */}
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4">Preview</h3>
              <div className="bg-white p-6 rounded-lg border">
                <ApprovalFlowVisual hierarchy={editingHierarchy} />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setIsEditing(false); setEditingHierarchy(null); }}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button className="btn-primary" onClick={handleSaveHierarchy} data-testid="save-hierarchy-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Hierarchy
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* View Mode */
        <>
          {/* Active Hierarchy Display */}
          {activeHierarchy && (
            <Card className="border-2 border-[#DC2626]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GitBranch className="w-5 h-5 text-[#DC2626]" />
                      {activeHierarchy.name}
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </CardTitle>
                    <CardDescription>
                      {activeHierarchy.description || 'Current approval workflow for leave requests'}
                    </CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => startEditing(activeHierarchy)} data-testid="edit-hierarchy-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ApprovalFlowVisual hierarchy={activeHierarchy} />
              </CardContent>
            </Card>
          )}

          {/* All Hierarchies List */}
          {hierarchies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>All Hierarchies for {getEntityName(selectedEntity)}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Levels</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {hierarchies.map(hierarchy => (
                      <TableRow key={hierarchy.hierarchy_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{hierarchy.name}</p>
                            <p className="text-sm text-slate-500">{hierarchy.description}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ApprovalFlowVisual hierarchy={hierarchy} compact />
                        </TableCell>
                        <TableCell>
                          {hierarchy.is_default ? (
                            <Badge className="bg-green-100 text-green-800">Default</Badge>
                          ) : (
                            <Badge variant="outline">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEditing(hierarchy)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            {!hierarchy.is_default && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-500"
                                onClick={() => handleDeleteHierarchy(hierarchy.hierarchy_id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
